import React, { useState, useMemo } from 'react';
import { StyleSheet, View, Image } from 'react-native';
import { useAuthStore, useToastStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import Badge from '@/components/Typography/Badge';
import useTranslation from '@/hooks/useTranslation';

const createStyles = (colors) => StyleSheet.create({
  container: {
    width: '100%',
  },
  inputWrapper: {
    gap: 8,
    width: '100%',
  },
  errorContainer: {
    marginBottom: 6,
    paddingVertical: 12,
    paddingLeft: 18,
    paddingRight: 12,
    borderRadius: 6,
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  qrContainer: {
    marginTop: 15,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  qrImage: {
    width: 150,
    height: 150,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  otpauthUrlContainer: {
    marginTop: 10,
    display: 'block',
    width: '100%',
  },
  codesContainer: {
    marginTop: 12,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
    gap: 6,
  },
});

export default function User2FAUpdateForm() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const { addToast } = useToastStore(state => state.actions);
  const isMfaEnabled = useAuthStore(state => state.user?.mfa?.isEnabled);
  const mfaMethod = useAuthStore(state => state.user?.mfa?.method);

  const startMfaTotpEnrollment = useAuthStore(state => state.startMfaTotpEnrollment);
  const verifyMfaTotpEnrollment = useAuthStore(state => state.verifyMfaTotpEnrollment);
  const cancelMfaTotpEnrollment = useAuthStore(state => state.cancelMfaTotpEnrollment);
  const disableMfa = useAuthStore(state => state.disableMfa);

  const [mfaError, setMfaError] = useState('');
  const [isStartingMfa, setIsStartingMfa] = useState(false);
  const [isVerifyingEnroll, setIsVerifyingEnroll] = useState(false);
  const [isCancelingMfa, setIsCancelingMfa] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [isDisablingMfa, setIsDisablingMfa] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [enrollmentData, setEnrollmentData] = useState(null); // { otpauthUrl, qrCodeDataUrl }
  const [backupCodes, setBackupCodes] = useState(null); // array of codes

  const showEnrollmentUI = !isMfaEnabled && !!enrollmentData;

  const handleStartEnrollment = async () => {
    try {
      setMfaError('');
      const pwd = currentPassword;
      if (!pwd) {
        addToast(t('forms.twoFactor.enterPassword'), 'error');
        return;
      }
      setCurrentPassword('');
      setIsStartingMfa(true);
      const data = await startMfaTotpEnrollment(pwd);
      setEnrollmentData({ otpauthUrl: data?.otpauthUrl, qrCodeDataUrl: data?.qrCodeDataUrl });
      addToast(t('forms.twoFactor.scanQrToast'), 'success');
    } catch (e) {
      setMfaError(e?.message || t('forms.twoFactor.enrollFailed'));
    } finally {
      setIsStartingMfa(false);
    }
  };

  const handleVerifyEnrollment = async () => {
    try {
      setMfaError('');
      if (!totpCode) {
        addToast(t('forms.twoFactor.enterCodeError'), 'error');
        return;
      }
      setIsVerifyingEnroll(true);
      const data = await verifyMfaTotpEnrollment(totpCode.trim());
      const codes = Array.isArray(data?.backupCodes) ? data.backupCodes : [];
      setBackupCodes(codes);
      setEnrollmentData(null);
      setTotpCode('');
      addToast(t('forms.twoFactor.enabledSuccess'), 'success');
    } catch (e) {
      setMfaError(e?.message || t('forms.twoFactor.verifyFailed'));
    } finally {
      setIsVerifyingEnroll(false);
    }
  };

  const handleCancelEnrollment = async () => {
    try {
      setMfaError('');
      setIsCancelingMfa(true);
      await cancelMfaTotpEnrollment();
      setEnrollmentData(null);
      setTotpCode('');
      addToast(t('forms.twoFactor.canceledSuccess'), 'success');
    } catch (e) {
      setMfaError(e?.message || t('forms.twoFactor.cancelFailed'));
    } finally {
      setIsCancelingMfa(false);
    }
  };

  const handleDisableMfa = async () => {
    try {
      setMfaError('');
      const pwd = currentPassword;
      if (!pwd) {
        addToast(t('forms.twoFactor.enterPassword'), 'error');
        return;
      }
      setCurrentPassword('');
      setIsDisablingMfa(true);
      await disableMfa(pwd, disableCode.trim().toUpperCase());
      setDisableCode('');
      setBackupCodes(null);
      setEnrollmentData(null);
      addToast(t('forms.twoFactor.disabledSuccess'), 'success');
    } catch (e) {
      setMfaError(e?.message || t('forms.twoFactor.disableFailed'));
    } finally {
      setIsDisablingMfa(false);
    }
  };

  return (
    <View style={styles.container}>
      {mfaError ? (
        <View style={styles.errorContainer}>
          <Text color='danger' size='n'>{mfaError}</Text>
        </View>
      ) : null}

      {!showEnrollmentUI && (
        <View style={styles.inputWrapper}>
          <FloatingLabelInput
            label={t('forms.twoFactor.currentPassword')}
            value={currentPassword}
            onChangeText={setCurrentPassword}
            placeholder={t('forms.twoFactor.currentPassword')}
            secureTextEntry
            isFullWidth
          />
        </View>
      )}

      {(!isMfaEnabled && !enrollmentData) ? (
        <Button
          text={t('forms.twoFactor.enableTitle')}
          variant={isStartingMfa || !currentPassword ? 'secondary' : 'primary'}
          isLoading={isStartingMfa}
          isDisabled={isStartingMfa || !currentPassword}
          onPress={handleStartEnrollment}
          style={{ marginTop: 8 }}
        />
      ) : null}

      {showEnrollmentUI ? (
        <View style={{ width: '100%' }}>
          <Text size='n'>
            <Text size='n' weight='bold'>1. </Text>
            {t('forms.twoFactor.scanQrStep')}
          </Text>
          <View style={styles.qrContainer}>
            {enrollmentData?.qrCodeDataUrl ? (
              <Image source={{ uri: enrollmentData.qrCodeDataUrl }} style={styles.qrImage} resizeMode="contain" />
            ) : (
              <Text size='n'>{t('forms.twoFactor.qrUnavailable')}</Text>
            )}
            {enrollmentData?.otpauthUrl ? (
              <View style={styles.otpauthUrlContainer}>
                <Text size='s' color='secondary'>{enrollmentData.otpauthUrl}</Text>
              </View>
            ) : null}
          </View>

          <Text size='n' style={{ marginTop: 15, marginBottom: 15 }}>
            <Text size='n' weight='bold'>2. </Text>
            {t('forms.twoFactor.enterCodeStep')}
          </Text>
          <View style={styles.inputWrapper}>
            <FloatingLabelInput
              label={t('forms.twoFactor.codeLabel')}
              value={totpCode}
              onChangeText={setTotpCode}
              placeholder="123456"
              keyboardType="number-pad"
              isFullWidth
            />
          </View>

          <View style={{ marginTop: 15, gap: 8, flexDirection: 'row' }}>
            <Button
              text={t('forms.twoFactor.verifyAndEnable')}
              variant='primary'
              isLoading={isVerifyingEnroll}
              isDisabled={isVerifyingEnroll || isCancelingMfa}
              onPress={handleVerifyEnrollment}
            />
            <Button
              text={t('common.cancel')}
              variant='secondary'
              isLoading={isCancelingMfa}
              isDisabled={isCancelingMfa || isVerifyingEnroll}
              onPress={handleCancelEnrollment}
            />
          </View>
        </View>
      ) : null}

      {Array.isArray(backupCodes) && backupCodes.length > 0 ? (
        <View style={styles.codesContainer}>
          <Text size='n' isBold>{t('forms.twoFactor.backupCodes')}</Text>
          <Text size='s' color='secondary'>{t('forms.twoFactor.backupCodesWarning')}</Text>
          <View style={{ marginTop: 6, flexDirection: 'row', gap: 16, width: '100%' }}>
            <View style={{ flex: 1, gap: 4 }}>
              {backupCodes.slice(0, 5).map((code, idx) => (
                <Text key={`c1-${idx}`} size='n'>{code}</Text>
              ))}
            </View>
            <View style={{ flex: 1, gap: 4 }}>
              {backupCodes.slice(5, 10).map((code, idx) => (
                <Text key={`c2-${idx}`} size='n'>{code}</Text>
              ))}
            </View>
          </View>
          <View style={{ display: 'flex', flexDirection: 'row', marginTop: 8 }}>
            <Button
              text={t('forms.twoFactor.savedBackupCodes')}
              variant='primary'
              onPress={() => setBackupCodes(null)}
            />
          </View>
        </View>
      ) : null}

      {isMfaEnabled ? (
        <View style={{ width: '100%', marginTop: 12, gap: 12 }}>
          <Text size='n'>{t('forms.twoFactor.status')} 
            <Badge
              text={`Enabled (${mfaMethod?.toUpperCase() || 'TOTP'})`}
              backgroundColor={colors.solid_color_tertiary}
              textColor='primary'
              style={{ marginLeft: 4 }}
            />
          </Text>

          <View style={{ gap: 8 }}>
            <FloatingLabelInput
              label={t('forms.twoFactor.disableCode')}
              value={disableCode}
              onChangeText={setDisableCode}
              placeholder="123456 / XXXX-XXXX"
              autoCapitalize="characters"
              autoCorrect={false}
              isFullWidth
            />
            <Button
              text={t('forms.twoFactor.disableTitle')}
              variant={currentPassword ? 'danger' : 'secondary'}
              isLoading={isDisablingMfa}
              isDisabled={isDisablingMfa || !currentPassword || !disableCode.trim()}
              onPress={handleDisableMfa}
            />
          </View>
        </View>
      ) : null}
    </View>
  );
} 